import React, { forwardRef } from "react";
import { Button } from "@purpur/button";
import type { BaseProps } from "@purpur/common-types";
import { Heading, type HeadingTagType } from "@purpur/heading";
import { Icon } from "@purpur/icon";
import { alertFilled } from "@purpur/icon/assets/alert-filled";
import { checkCircleFilled } from "@purpur/icon/assets/check-circle-filled";
import { close } from "@purpur/icon/assets/close";
import { errorFilled } from "@purpur/icon/assets/error-filled";
import { infoFilled } from "@purpur/icon/assets/info-filled";
import { sparkles } from "@purpur/icon/assets/sparkles";
import { Paragraph } from "@purpur/paragraph";
import c from "classnames/bind";

import styles from "./notification.module.scss";

const cx = c.bind(styles);

export const NOTIFICATION_STATUS = {
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
  INFO: "info",
  AI: "ai",
} as const;

export const notificationStatuses = Object.values(NOTIFICATION_STATUS);
export type NotificationStatus = (typeof NOTIFICATION_STATUS)[keyof typeof NOTIFICATION_STATUS];

export const NOTIFICATION_ARIA_LIVE = {
  POLITE: "polite",
  ASSERTIVE: "assertive",
} as const;

export const notificationAriaLiveValues = Object.values(NOTIFICATION_ARIA_LIVE);
export type NotificationAriaLive =
  (typeof NOTIFICATION_ARIA_LIVE)[keyof typeof NOTIFICATION_ARIA_LIVE];

export const NOTIFICATION_ROLE = {
  ALERT: "alert",
  DIALOG: "dialog",
  ALERTDIALOG: "alertdialog",
} as const;

export const notificationRoles = Object.values(NOTIFICATION_ROLE);
export type NotificationRole = (typeof NOTIFICATION_ROLE)[keyof typeof NOTIFICATION_ROLE];

const rootClassName = "purpur-notification";

const getStatusIcon = (status: NotificationStatus) => {
  switch (status) {
    case NOTIFICATION_STATUS.AI:
      return sparkles;
    case NOTIFICATION_STATUS.INFO:
      return infoFilled;
    case NOTIFICATION_STATUS.SUCCESS:
      return checkCircleFilled;
    case NOTIFICATION_STATUS.WARNING:
      return alertFilled;
    case NOTIFICATION_STATUS.ERROR:
      return errorFilled;
  }
};

type DefaultProps = BaseProps & {
  ["aria-live"]?: NotificationAriaLive;
  headingTag?: HeadingTagType;
  status?: NotificationStatus;
  heading?: string;
  role?: NotificationRole;
  hidden?: boolean;
  /**
   * Use to explicitly not open the notification initially and open it programatically to trigger
   * that the aria-live message is read by the screen reader correctly.
   */
  open?: boolean;
  hideIcon?: boolean;
};

type CloseButtonProps = {
  onClose: () => void;
  closeButtonAriaLabel: string;
};

type NoCloseButtonProps = {
  onClose?: never;
  closeButtonAriaLabel?: never;
};

export type NotificationProps = DefaultProps & (CloseButtonProps | NoCloseButtonProps);

export const Notification = forwardRef<HTMLDivElement, NotificationProps>(
  (
    {
      ["data-testid"]: dataTestId,
      ["aria-live"]: ariaLive,
      children,
      className,
      closeButtonAriaLabel,
      headingTag = "h4",
      status = "info",
      heading,
      hidden,
      id,
      onClose,
      open = true,
      hideIcon,
      ...props
    },
    ref
  ) => {
    const showHeader = !!heading || !hideIcon;

    return (
      <div aria-live={ariaLive}>
        {open && (
          <div
            id={id}
            aria-labelledby={id ? `${id}-heading` : undefined}
            aria-describedby={id ? `${id}-body` : undefined}
            aria-hidden={!!hidden}
            className={cx(className, rootClassName, `${rootClassName}--${status}`, {
              [`${rootClassName}--hidden`]: hidden,
              [`${rootClassName}--has-close-button`]: !!onClose,
            })}
            data-testid={dataTestId}
            ref={ref}
            {...props}
          >
            {showHeader && (
              <div className={cx(`${rootClassName}__top`)}>
                <div className={cx(`${rootClassName}__header`)}>
                  {!hideIcon && (
                    <Icon
                      data-testid={dataTestId ? `${dataTestId}-icon` : undefined}
                      className={cx(`${rootClassName}__icon`)}
                      svg={getStatusIcon(status)}
                      size="sm"
                    />
                  )}
                  {!!heading && (
                    <Heading
                      data-testid={dataTestId ? `${dataTestId}-heading` : undefined}
                      variant="subsection-100"
                      tag={headingTag}
                      className={cx(`${rootClassName}__heading`)}
                    >
                      <span id={id ? `${id}-heading` : undefined}> {heading}</span>
                    </Heading>
                  )}
                </div>
              </div>
            )}
            {onClose && (
              <Button
                data-testid={dataTestId ? `${dataTestId}-close-button` : undefined}
                variant="text"
                size="sm"
                iconOnly
                aria-label={closeButtonAriaLabel}
                onClick={onClose}
                className={cx(`${rootClassName}__close-button`)}
              >
                <Icon svg={close} size="md" />
              </Button>
            )}
            {children && (
              <div
                data-testid={dataTestId ? `${dataTestId}-body` : undefined}
                className={cx(`${rootClassName}__body`, {
                  [`${rootClassName}__body--extra-padding`]: !showHeader && !!onClose,
                })}
                id={id ? `${id}-body` : undefined}
              >
                {typeof children === "string" ? (
                  <Paragraph
                    data-testid={dataTestId ? `${dataTestId}-paragraph` : undefined}
                    variant="paragraph-100"
                  >
                    {children}
                  </Paragraph>
                ) : (
                  children
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

Notification.displayName = "Notification";
